import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import PageHeader from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { toast } from "sonner";
import { Trash2, Mail, MailOpen, Phone, Building, Truck, Clock, MessageSquare } from "lucide-react";

export default function InquiriesPage() {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const fetchInquiries = () => {
    axios.get(`${API}/inquiries`).then(res => { setInquiries(res.data); setLoading(false); }).catch(() => setLoading(false));
  };
  useEffect(() => { fetchInquiries(); }, []);

  const markAs = async (id, status) => {
    try {
      await axios.put(`${API}/inquiries/${id}/status?status=${status}`);
      toast.success(`Marked as ${status}`);
      fetchInquiries();
    } catch { toast.error("Failed to update"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this inquiry?")) return;
    try { await axios.delete(`${API}/inquiries/${id}`); toast.success("Deleted"); fetchInquiries(); if (selected?.id === id) setSelected(null); } catch { toast.error("Failed"); }
  };

  const newCount = inquiries.filter(i => i.status === "new").length;

  return (
    <div data-testid="inquiries-page">
      <PageHeader title="Inquiries" subtitle={`${inquiries.length} total${newCount > 0 ? ` / ${newCount} new` : ""}`} />

      <div className="p-6">
        {loading ? (
          <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" /></div>
        ) : inquiries.length === 0 ? (
          <div className="text-center py-20">
            <MessageSquare className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
            <p className="text-zinc-400 text-sm">No inquiries yet. Enquiries from your website will appear here.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
            {/* List */}
            <div className="lg:col-span-2 border border-zinc-800 rounded-sm overflow-hidden">
              <div className="divide-y divide-zinc-800/50 max-h-[75vh] overflow-y-auto">
                {inquiries.map((inq) => (
                  <button
                    key={inq.id}
                    data-testid={`inquiry-item-${inq.id}`}
                    onClick={() => { setSelected(inq); if (inq.status === "new") markAs(inq.id, "read"); }}
                    className={`w-full text-left p-4 hover:bg-zinc-800/30 transition-colors ${selected?.id === inq.id ? "bg-zinc-800/40" : ""}`}
                  >
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <div className="flex items-center gap-2">
                        {inq.status === "new" ? <Mail className="w-3.5 h-3.5 text-orange-500 flex-shrink-0" /> : <MailOpen className="w-3.5 h-3.5 text-zinc-500 flex-shrink-0" />}
                        <span className={`text-sm font-medium ${inq.status === "new" ? "text-white" : "text-zinc-400"}`}>{inq.name}</span>
                      </div>
                      {inq.status === "new" && <span className="w-2 h-2 rounded-full bg-orange-500 flex-shrink-0 mt-1.5" />}
                    </div>
                    <p className="text-xs text-zinc-500 truncate ml-5.5 pl-[22px]">{inq.message}</p>
                    <div className="flex items-center gap-2 mt-1.5 pl-[22px]">
                      {inq.service && <span className="text-[10px] text-zinc-500 bg-zinc-800 px-1.5 py-0.5 rounded-sm capitalize">{inq.service.replace("_", " ")}</span>}
                      <span className="text-[10px] text-zinc-600">{new Date(inq.created_at).toLocaleDateString()}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Detail */}
            <div className="lg:col-span-3">
              {selected ? (
                <div className="border border-zinc-800 bg-[#18181B] rounded-sm" data-testid="inquiry-detail">
                  <div className="p-5 border-b border-zinc-800">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-heading text-lg font-bold text-white">{selected.name}</h3>
                      <div className="flex items-center gap-2">
                        {selected.status !== "replied" && (
                          <button data-testid="mark-replied" onClick={() => markAs(selected.id, "replied")}
                            className="text-xs text-emerald-500 border border-emerald-500/20 px-2 py-1 rounded-sm hover:bg-emerald-500/10">
                            Mark Replied
                          </button>
                        )}
                        <button data-testid={`delete-inquiry-${selected.id}`} onClick={() => handleDelete(selected.id)}
                          className="text-zinc-500 hover:text-red-400 p-1"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      {selected.email && (
                        <div className="flex items-center gap-2">
                          <Mail className="w-3.5 h-3.5 text-zinc-500" />
                          <a href={`mailto:${selected.email}`} className="text-sm text-orange-400 hover:text-orange-300">{selected.email}</a>
                        </div>
                      )}
                      {selected.phone && (
                        <div className="flex items-center gap-2">
                          <Phone className="w-3.5 h-3.5 text-zinc-500" />
                          <a href={`tel:${selected.phone}`} className="text-sm text-orange-400 hover:text-orange-300">{selected.phone}</a>
                        </div>
                      )}
                      {selected.company && (
                        <div className="flex items-center gap-2">
                          <Building className="w-3.5 h-3.5 text-zinc-500" />
                          <span className="text-sm text-zinc-300">{selected.company}</span>
                        </div>
                      )}
                      {selected.service && (
                        <div className="flex items-center gap-2">
                          <Truck className="w-3.5 h-3.5 text-zinc-500" />
                          <span className="text-sm text-zinc-300 capitalize">{selected.service.replace("_", " ")}</span>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                      <Clock className="w-3 h-3 text-zinc-600" />
                      <span className="text-[10px] text-zinc-500">{new Date(selected.created_at).toLocaleString()}</span>
                      <StatusBadge status={selected.status === "new" ? "pending" : selected.status === "replied" ? "delivered" : "assigned"} />
                    </div>
                  </div>
                  <div className="p-5">
                    <h4 className="text-xs text-zinc-500 uppercase tracking-wider font-medium mb-2">Message</h4>
                    <p className="text-sm text-zinc-300 leading-relaxed whitespace-pre-wrap">{selected.message}</p>
                  </div>
                  <div className="p-5 border-t border-zinc-800 flex gap-2">
                    {selected.email && (
                      <a href={`mailto:${selected.email}?subject=RE: Quote Request - SGHK Logistics`}
                        data-testid="reply-email-button"
                        className="h-9 px-4 bg-orange-500 hover:bg-orange-600 text-white text-xs font-medium uppercase tracking-wider rounded-[2px] flex items-center gap-1.5 transition-colors">
                        <Mail className="w-3.5 h-3.5" /> Reply via Email
                      </a>
                    )}
                    {selected.phone && (
                      <a href={`tel:${selected.phone}`}
                        data-testid="call-button"
                        className="h-9 px-4 border border-zinc-700 text-zinc-300 text-xs font-medium uppercase tracking-wider rounded-[2px] flex items-center gap-1.5 hover:bg-zinc-800">
                        <Phone className="w-3.5 h-3.5" /> Call
                      </a>
                    )}
                  </div>
                </div>
              ) : (
                <div className="border border-zinc-800 bg-[#18181B] rounded-sm flex items-center justify-center h-64">
                  <div className="text-center">
                    <MessageSquare className="w-8 h-8 text-zinc-700 mx-auto mb-2" />
                    <p className="text-zinc-500 text-sm">Select an inquiry to view details</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
